import React, { useEffect, useState } from 'react';
import { useFooter } from '../hooks/useContentful';
import jsPDF from 'jspdf';

const Footer = () => {
  // TODO: When Contentful is connected, use this for footer content
  const {footerData, loading, error } = useFooter();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  // Function to generate and download PDF
  const downloadPDF = (label, content) => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Add title
      pdf.setFontSize(16);
      pdf.text(label, 20, 20);

      // Add horizontal line
      pdf.setDrawColor(147, 51, 234); // Purple color
      pdf.line(20, 30, 190, 30);

      // Add content
      pdf.setFontSize(11);
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;

      // Split text to fit page width
      const splitText = pdf.splitTextToSize(content, maxWidth);
      const lineHeight = 7;
      let yPosition = 40;

      splitText.forEach((line) => {
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.text(line, margin, yPosition);
        yPosition += lineHeight;
      });

      // Add footer
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(
        `Generado por CEV24 - ${new Date().toLocaleDateString()}`,
        20,
        pageHeight - 10
      );

      // Download the PDF
      pdf.save(`${label.replace(/\s+/g, '_')}_CEV24.pdf`);
    } catch (error) {
      alert('Error al generar el PDF. Intenta nuevamente.');
    }
  };

  // Function to extract plain text from Contentful Rich Text
  const extractTextFromRichText = (richText) => {
    if (typeof richText === 'string') {
      return richText;
    }

    if (!richText || typeof richText !== 'object') {
      return '';
    }

    let text = '';

    try {
      // Iterar sobre el contenido principal
      if (richText.content && Array.isArray(richText.content)) {
        richText.content.forEach((node) => {
          // Saltar nodos vacíos o separadores
          if (!node || node.nodeType === 'hr') return;

          // Si el nodo tiene contenido anidado (paragraph, heading, etc.)
          if (node.content && Array.isArray(node.content)) {
            node.content.forEach((textNode) => {
              // Buscar nodos de tipo "text"
              if (textNode.nodeType === 'text' && textNode.value) {
                text += textNode.value;
              }
            });
          }

          // Añadir salto de línea después de cada elemento
          if (text && !text.endsWith('\n')) {
            text += '\n';
          }
        });
      }

      return text.trim();
    } catch (error) {
      return '';
    }
  };

  // TODO: Replace with Contentful data: socialMedia links, contact info, etc.

  // Use Contentful data if available, otherwise use empty array for social media
  const socialMediaLinks = footerData?.socialMedia || [];

  const nagivatorConst = {
    navigation: [
      { href: '#inicio', label: 'Inicio' },
      { href: '#servicios', label: 'Servicios' },
      { href: '#instalaciones', label: 'Instalaciones' },
      { href: '#nosotros', label: 'Sobre Nosotros' },
      { href: '#contacto', label: 'Contacto' }
    ],
  };

  // Handle smooth scrolling for footer navigation
  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetElement = document.querySelector(href);
    if (targetElement) {
      const navbar = document.getElementById('navbar');
      const offset = navbar ? navbar.offsetHeight : 0;
      
      window.scrollTo({
        top: targetElement.offsetTop - offset,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <footer className="bg-gray-900 text-white relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[...Array(4)].map((_, index) => (
              <div key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="h-6 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg w-3/4 mb-4 animate-shimmer-purple"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gradient-to-r from-purple-500/50 to-purple-600/50 rounded animate-shimmer-purple"></div>
                  <div className="h-4 bg-gradient-to-r from-purple-500/50 to-purple-600/50 rounded w-5/6 animate-shimmer-purple"></div>
                  <div className="h-4 bg-gradient-to-r from-purple-500/50 to-purple-600/50 rounded w-4/5 animate-shimmer-purple"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    );
  }

  if (error) {
    // Fallback to static content
  }

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-500/5 rounded-full -ml-36 -mb-36 blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="mb-6 md:mb-0">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                <i className="fas fa-paw text-white text-2xl md:text-3xl"></i>
              </div>
              <span className="ml-4 text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">CEV24</span>
            </div>
            <p className="text-gray-300 mt-3 text-base md:text-lg leading-relaxed">
              {footerData?.description }
            </p>
          </div>

          {/* Navigation */}
          <div>
            <div className="inline-flex items-center gap-2 bg-purple-100/20 text-purple-300 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              <i className="fas fa-compass"></i>
              Navegación
            </div>
            <ul className="space-y-3">
              {nagivatorConst.navigation.map((item, index) => (
                <li key={index}>
                  <a 
                    href={item.href} 
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="text-gray-300 hover:text-white hover:text-purple-300 transition-all duration-300 text-base md:text-lg flex items-center group"
                  >
                    <i className="fas fa-chevron-right text-xs mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

         {/* Schedule */}
<div className="sm:col-span-2 md:col-span-1">
  <div className="inline-flex items-center gap-2 bg-purple-100/20 text-purple-300 px-3 py-1 rounded-full text-sm font-semibold mb-4">
    <i className="fas fa-clock"></i>
    Horarios de Atención
  </div>
  <ul className="space-y-3 text-gray-300 text-base md:text-lg">
    {(footerData?.schedule || []).map((item, index) => {
      const fields = item?.fields || item || {};
      return (
      <li 
        key={index} 
        className={`flex justify-between items-center gap-2 p-2 rounded-lg transition-all duration-300 ${
          fields.highlight === 'true' 
            ? 'bg-gradient-to-r from-red-600/15 to-orange-600/15 border-l-3 border-red-500 shadow-md shadow-red-500/10' 
            : 'hover:bg-purple-800/10'
        }`}
      >
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
            fields.highlight === 'true' 
              ? 'bg-red-500 shadow-sm' 
              : 'bg-purple-600/20'
          }`}>
            <i className={`${fields.icon} text-xs ${
              fields.highlight === 'true' 
                ? 'text-white' 
                : 'text-purple-400'
            }`}></i>
          </div>
          <span className={`text-sm md:text-base font-medium ${
            fields.highlight === 'true' 
              ? 'text-white' 
              : 'text-gray-300'
          }`}>
            {fields.service}
          </span>
        </div>
        <span className={`text-sm md:text-base font-semibold ${
          fields.highlight === 'true' 
            ? 'text-red-400' 
            : 'text-purple-300'
        }`}>
          {fields.time}
        </span>
      </li>
      );
    })}
  </ul>
</div>

          {/* Social Media */}
   <div className="sm:col-span-2 md:col-span-1">
            <div className="inline-flex items-center gap-2 bg-purple-100/20 text-purple-300 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              <i className="fas fa-share-alt"></i>
              Síguenos
            </div>
            <div className="flex space-x-4 md:space-x-6">
              {socialMediaLinks.map((social, index) => {
                const url = social.fields?.url;
                const platform = social.fields?.platform;
                const icon = social.fields?.icon;
                
                if (!url || !icon) {
                  return null;
                }
                
                return (
                  <a 
                    key={index}
                    href={url}
                    className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
                    target="_blank"
                    rel="noopener noreferrer"
                    title={platform || 'Síguenos'}
                  >
                    <i className={`${icon} text-xl md:text-2xl text-white`}></i>
                  </a>
                );
              })}
            </div>
            <p className="text-gray-300 mt-6 text-base md:text-lg leading-relaxed">
              Síguenos en nuestras redes para consejos de salud, noticias y momentos adorables de nuestros pacientes.
            </p>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-purple-800/50 mt-12 md:mt-16 pt-8 md:pt-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8">
            {/* Copyright */}
            <div className="text-center md:text-left flex-1">
              <p className="text-gray-400 text-xs md:text-sm font-medium leading-relaxed">
                <span className="font-bold text-gray-300">&copy; {currentYear}</span> CEV24 - Clínica de Emergencias Veterinarias. Todos los derechos reservados.
              </p>
            </div>

            {/* Legal Links with animated underline */}
            <div className="flex flex-wrap justify-center md:justify-end gap-3 md:gap-4">
              {(footerData?.legalLinks || []).map((link, index) => {
                const fields = link?.fields || link || {};
                
                // Extraer texto plano del contenido Rich Text
                const contentText = extractTextFromRichText(fields.content);
                
                return (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (contentText && contentText.length > 0) {
                        downloadPDF(fields.label, contentText);
                      }
                    }}
                    className="relative text-gray-400 hover:text-purple-300 text-xs md:text-sm transition-colors duration-300 group cursor-pointer bg-none border-none p-0 font-inherit"
                  >
                    {fields.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 group-hover:w-full"></span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Devside Attribution */}
          <div className="mt-8 pt-8 border-t border-purple-800/30 text-center">
            <p className="text-gray-500 text-xs md:text-sm">
              Developed by 
              <a 
                href="https://devside.com.ar" 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-1 text-purple-400 hover:text-purple-300 font-semibold transition-colors duration-300 inline-flex items-center gap-1"
              >
                Devside
                <i className="fas fa-external-link-alt text-xs"></i>
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;